import React, {useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {DataGrid, GridToolbar} from '@mui/x-data-grid';
import {Badge, Card, Col, ProgressBar, Row, Spinner} from 'react-bootstrap';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {baseUrl} from '../Univ';
function BudgetVsActual() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // =========================================
    // FETCH DATA
    // =========================================
    useEffect(() => {
        fetchData();
    }, []);
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${baseUrl}budget-vs-actual/`
            );
            const data = response.data || [];
            const formattedRows = data.map((item, index) => ({
                id: item.id || index + 1,
                budget_name: item.budget || '-',
                department_name: item.department || '-',
                account: item.account || '-',
                budget_type: item.budget_type || '-',
                yearly_budget: Number(item.yearly_budget || 0),
                actual: Number(item.actual || 0),
                variance: Number(item.variance || 0),
                utilization: Number(item.utilization || 0),
                status: item.status || 'SAFE',
            }));
            setRows(formattedRows);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };
    // =========================================
    // SUMMARY
    // =========================================
    const summary = useMemo(() => {
        const totalBudget = rows.reduce(
            (sum, row) => sum + row.yearly_budget,
            0
        );
        const totalActual = rows.reduce(
            (sum, row) => sum + row.actual,
            0
        );
        const totalVariance = rows.reduce(
            (sum, row) => sum + row.variance,
            0
        );
        const utilization = totalBudget > 0
            ? ((totalActual / totalBudget) * 100).toFixed(2)
            : 0;
        return {
            totalBudget,
            totalActual,
            totalVariance,
            utilization,
        };
    }, [rows]);
    // =========================================
    // BAR CHART DATA
    // =========================================
    const chartData = useMemo(() => {
        return rows.slice(0, 10).map((row) => ({
            name: row.account,
            Budget: row.yearly_budget,
            Actual: row.actual,
        }));
    }, [rows]);
    // =========================================
    // PIE CHART DATA
    // =========================================
    const pieData = useMemo(() => {
        return [
            {
                name: 'Budget',
                value: summary.totalBudget,
            },
            {
                name: 'Actual',
                value: summary.totalActual,
            },
        ];
    }, [summary]);
    const COLORS = ['#0d6efd', '#20c997'];
    // =========================================
    // DATAGRID COLUMNS
    // =========================================
    const columns = [
        {
            field: 'budget_name',
            headerName: 'Budget',
            width: 180,
        },
        {
            field: 'department_name',
            headerName: 'Department',
            width: 180,
        },
        {
            field: 'account',
            headerName: 'Account Head',
            width: 220,
        },
        {
            field: 'budget_type',
            headerName: 'Type',
            width: 120,
        },
        {
            field: 'yearly_budget',
            headerName: 'Budget',
            width: 150,
            renderCell: (params) => (
                <strong>
                    ₹ {params.value.toLocaleString()}
                </strong>
            ),
        },
        {
            field: 'actual',
            headerName: 'Actual',
            width: 150,
            renderCell: (params) => (
                <strong className="text-success">
                    ₹ {params.value.toLocaleString()}
                </strong>
            ),
        },
        {
            field: 'variance',
            headerName: 'Variance',
            width: 150,
            renderCell: (params) => (
                <strong className="text-danger">
                    ₹ {params.value.toLocaleString()}
                </strong>
            ),
        },
        {
            field: 'utilization',
            headerName: 'Utilization %',
            width: 220,
            renderCell: (params) => (
                <div style={{width: '100%'}}>

                    <ProgressBar
                        now={params.value}
                        label={`${params.value}%`}
                        variant={
                            params.value > 90
                                ? 'danger'
                                : params.value > 70
                                    ? 'warning'
                                    : 'success'
                        }
                    />

                </div>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <>

                    {
                        params.value === 'OVER BUDGET'
                            ?
                            <Badge bg="danger">
                                OVER BUDGET
                            </Badge>
                            :
                            params.value === 'NEAR LIMIT'
                                ?
                                <Badge bg="warning">
                                    NEAR LIMIT
                                </Badge>
                                :
                                <Badge bg="success">
                                    SAFE
                                </Badge>
                    }

                </>
            ),
        },
    ];
    // =========================================
    // LOADING
    // =========================================
    if (loading) {
        return (
            <div className="text-center mt-5">

                <Spinner animation="border" variant="primary"/>

                <h5 className="mt-3">
                    Loading Budget Analytics...
                </h5>

            </div>
        );
    }
    // =========================================
    // UI
    // =========================================
    return (
        <section className="container-fluid mt-4">

            <h3 className="text-center bg-dark text-white p-3 rounded shadow">

                <i className="fa-solid fa-chart-line me-2"></i>

                Budget Vs Actual KPI Dashboard

            </h3>

            {/* KPI CARDS */}

            <Row className="mt-4 g-3">

                <Col md={3}>

                    <Card className="shadow border-0">

                        <Card.Body>

                            <h6>Total Budget</h6>

                            <h3 className="text-primary">

                                ₹ {summary.totalBudget.toLocaleString()}

                            </h3>

                        </Card.Body>

                    </Card>

                </Col>

                <Col md={3}>

                    <Card className="shadow border-0">

                        <Card.Body>

                            <h6>Total Actual</h6>

                            <h3 className="text-success">

                                ₹ {summary.totalActual.toLocaleString()}

                            </h3>

                        </Card.Body>

                    </Card>

                </Col>

                <Col md={3}>

                    <Card className="shadow border-0">

                        <Card.Body>

                            <h6>Total Variance</h6>

                            <h3 className="text-danger">

                                ₹ {summary.totalVariance.toLocaleString()}

                            </h3>

                        </Card.Body>

                    </Card>

                </Col>

                <Col md={3}>

                    <Card className="shadow border-0">

                        <Card.Body>

                            <h6>Budget Utilization</h6>

                            <h3 className="text-warning">

                                {summary.utilization}%

                            </h3>

                            <ProgressBar
                                now={summary.utilization}
                                variant="warning"
                            />

                        </Card.Body>

                    </Card>

                </Col>

            </Row>

            {/* CHARTS */}

            <Row className="mt-4 g-3">

                <Col md={8}>

                    <Card className="shadow border-0">

                        <Card.Header className="bg-primary text-white">

                            Budget vs Actual Analysis

                        </Card.Header>

                        <Card.Body>

                            <ResponsiveContainer width="100%" height={350}>

                                <BarChart data={chartData}>

                                    <CartesianGrid strokeDasharray="3 3"/>

                                    <XAxis dataKey="name"/>

                                    <YAxis/>

                                    <Tooltip/>

                                    <Legend/>

                                    <Bar
                                        dataKey="Budget"
                                        fill="#0d6efd"
                                    />

                                    <Bar
                                        dataKey="Actual"
                                        fill="#198754"
                                    />

                                </BarChart>

                            </ResponsiveContainer>

                        </Card.Body>

                    </Card>

                </Col>

                <Col md={4}>

                    <Card className="shadow border-0">

                        <Card.Header className="bg-success text-white">

                            Budget Distribution

                        </Card.Header>

                        <Card.Body>

                            <ResponsiveContainer width="100%" height={350}>

                                <PieChart>

                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        outerRadius={120}
                                        label
                                    >

                                        {
                                            pieData.map((entry, index) => (
                                                <Cell
                                                    key={index}
                                                    fill={COLORS[index % COLORS.length]}
                                                />
                                            ))
                                        }

                                    </Pie>

                                    <Tooltip/>

                                    <Legend/>

                                </PieChart>

                            </ResponsiveContainer>

                        </Card.Body>

                    </Card>

                </Col>

            </Row>

            {/* TABLE */}

            <Card className="shadow border-0 mt-4 mb-5">

                <Card.Header className="bg-secondary text-white">

                    Detailed Budget Analytics

                </Card.Header>

                <Card.Body>

                    <div style={{width: '100%'}}>

                        <DataGrid

                            rows={rows}

                            columns={columns}

                            loading={loading}

                            autoHeight

                            pageSizeOptions={[10, 25, 50]}

                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}

                            slots={{
                                toolbar: GridToolbar,
                            }}

                            sx={{
                                border: 0,
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f8f9fa',
                                    fontWeight: 'bold',
                                },
                            }}

                        />

                    </div>

                </Card.Body>

            </Card>

        </section>
    );
}
export default BudgetVsActual;